import React, { Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Upload, Tags, Inbox, History, FileText, Loader2 } from "lucide-react";
import { usePendingCount } from "@/hooks/usePendingSubmissions";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useOrganization } from "@/contexts/OrganizationContext";

// Lazy load tab components to prevent one failing tab from breaking others
const LibraryManager = React.lazy(() => import("./LibraryManager"));
const MassUpload = React.lazy(() => import("./MassUpload"));
const TagsManager = React.lazy(() => import("./Tags"));
const PendingSubmissions = React.lazy(() => import("./PendingSubmissions"));
const LibraryHistory = React.lazy(() => import("./LibraryHistory"));
const Pages = React.lazy(() => import("./Pages"));

const TabLoadingFallback = () => (
  <div className="flex items-center justify-center p-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const LibraryTabs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "items";
  const { count: pendingCount, refresh: refreshPendingCount } = usePendingCount();
  const { organization } = useOrganization();
  
  const contentLabel = organization?.slug === 'spes' ? 'Repository' : 'Content';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="h-full">
      <div className="px-4 pt-4 md:px-8 md:pt-6">
        <h1 className="text-2xl font-bold font-display mb-4">{contentLabel}</h1>
      </div>
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="px-4 md:px-8">
          <TabsList className="w-full md:w-auto grid grid-cols-6 md:inline-flex">
            <TabsTrigger value="items" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Items</span>
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2 relative">
              <Inbox className="h-4 w-4" />
              <span className="hidden sm:inline">Pending</span>
              {pendingCount > 0 && (
                <Badge 
                  className="ml-1 h-5 min-w-[20px] px-1.5 bg-amber-500 text-amber-950 hover:bg-amber-500"
                >
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="mass-upload" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Upload</span>
            </TabsTrigger>
            <TabsTrigger value="tags" className="flex items-center gap-2">
              <Tags className="h-4 w-4" />
              <span className="hidden sm:inline">Tags</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
            <TabsTrigger value="pages" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Pages</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="items" className="mt-0">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <LibraryManager embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="pending" className="mt-0">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <PendingSubmissions embedded onCountChange={refreshPendingCount} />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="mass-upload" className="mt-0">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <MassUpload embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="tags" className="mt-0">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <TagsManager embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="history" className="mt-0">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <LibraryHistory embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="pages" className="mt-0">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <Pages embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LibraryTabs;
